import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
    Form, FormField, FormItem, FormLabel, FormControl, FormMessage,
} from '@/components/ui/form';
import { Button } from '@/components/ui/button';
import { walletSchema } from '../utils/validation';
import { WALLETS } from '../types/index';
import { cn } from '@/lib/utils';

/**
 * Wallet payment form with visual wallet option grid.
 * @param {{ onSubmit: Function, isProcessing: boolean }} props
 */
export function WalletForm({ onSubmit, isProcessing }) {
    const form = useForm({
        resolver: zodResolver(walletSchema),
        defaultValues: { walletId: '' },
        mode: 'onTouched',
    });

    const selected = form.watch('walletId');

    return (
        <div className="space-y-5" data-testid="wallet-form">
            <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} noValidate className="space-y-5" aria-label="Wallet payment form">
                    <FormField
                        control={form.control}
                        name="walletId"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>Select Wallet</FormLabel>
                                <div
                                    className="grid grid-cols-2 sm:grid-cols-3 gap-3"
                                    role="radiogroup"
                                    aria-label="Wallet options"
                                >
                                    {WALLETS.map((wallet) => (
                                        <button
                                            key={wallet.id}
                                            type="button"
                                            role="radio"
                                            aria-checked={field.value === wallet.id}
                                            onClick={() => { field.onChange(wallet.id); form.trigger('walletId'); }}
                                            data-testid={`wallet-${wallet.id}`}
                                            className={cn(
                                                'relative flex flex-col items-center gap-2 p-4 border rounded-sm text-sm font-medium transition-all duration-200',
                                                field.value === wallet.id
                                                    ? 'border-[#F87217] bg-[#F87217]/5 text-[#F87217]'
                                                    : 'border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-slate-50'
                                            )}
                                        >
                                            {/* Wallet color dot */}
                                            <span
                                                className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold"
                                                style={{ backgroundColor: wallet.color }}
                                                aria-hidden="true"
                                            >
                                                {wallet.name.charAt(0)}
                                            </span>
                                            {wallet.name}
                                            {field.value === wallet.id && (
                                                <span className="absolute top-2 right-2 w-2 h-2 bg-[#F87217] rounded-full" aria-hidden="true" />
                                            )}
                                        </button>
                                    ))}
                                </div>
                                <FormMessage />
                            </FormItem>
                        )}
                    />

                    {selected && (
                        <p className="text-sm text-slate-500 bg-slate-50 border border-slate-200 p-3 rounded-sm">
                            You will be redirected to <strong>{WALLETS.find(w => w.id === selected)?.name}</strong> to complete the payment.
                        </p>
                    )}

                    <Button
                        type="submit"
                        disabled={isProcessing}
                        className="w-full bg-[#F87217] hover:bg-[#C65B12] text-white rounded-sm btn-primary-glow font-semibold py-6"
                        data-testid="submit-wallet"
                    >
                        {isProcessing ? (
                            <span className="flex items-center gap-2">
                                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                Redirecting...
                            </span>
                        ) : 'Pay via Wallet'}
                    </Button>
                </form>
            </Form>
        </div>
    );
}

export default WalletForm;
